<script setup lang="ts">
  import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
    Popover,
    PopoverAnchor,
    PopoverContent
  } from '@gat-ship/vue-components'
  import {
    IconCheck,
    IconChevronDown,
    IconEstimatedTime,
    IconTimeOfDay
  } from '@gat-ship/icons'
  import { ListboxFilter } from 'reka-ui'
  import type { EstimatedTimeNameRef } from '@gat-ship/types'
  import { useSelectEstimatedTimeDataSource } from '../../composables/useSelectEstimatedTimeDataSource'
  import {
    classifyEstimatedTimeInput,
    displayEstimatedTimeChoice,
    filterEstimatedTimeEntries,
    resolveEstimatedTimeChoice,
    type EstimatedTimeChoice
  } from './resolve-estimated-time-choice'

  /**
   * A combined estimated-time field: one input where **typing a clock time**
   * (leading digit) records an `exact` time and **typing letters** searches
   * the named estimated-time registry, with arrow/Enter selection from the
   * dropdown. Backed by `GET /ref-data/time/estimated-names`; matches on the
   * entry `name` (the lean ref shape carries no code).
   *
   * Emits an {@link EstimatedTimeChoice}: `exact` | `named` | `invalid`
   * (kept draft, shown with a destructive ring) | `undefined` (empty). The
   * consumer maps that onto its own model (e.g. create-portcall's ETA field).
   * Owns no label/error chrome.
   */
  const props = withDefaults(
    defineProps<{
      modelValue?: EstimatedTimeChoice
      id?: string
      placeholder?: string
      dataTestid?: string
      disabled?: boolean
      /** Force the destructive ring (e.g. a form-level submit error). */
      ariaInvalid?: boolean
      /** Anchor sizing — Tailwind classes. Defaults to `h-9 w-44`. */
      triggerClass?: string
    }>(),
    { placeholder: 'hh:mm', triggerClass: 'h-9 w-44' }
  )

  const emit = defineEmits<{
    'update:modelValue': [value: EstimatedTimeChoice]
    blur: []
  }>()

  const {
    estimatedTimeNames,
    loadingEstimatedTimeNames,
    fetchEstimatedTimeNames
  } = useSelectEstimatedTimeDataSource()

  // Lazy fetch on first render.
  fetchEstimatedTimeNames()

  const entries = computed(() => estimatedTimeNames.value ?? [])

  const isOpen = ref(false)
  const anchorEl = useTemplateRef<HTMLElement>('anchorEl')
  const inputEl = useTemplateRef<HTMLInputElement>('inputEl')
  // Reka-ui's ListboxRoot auto-highlights an item on mount and calls
  // `.focus()` on the highlighted node — stealing focus from our external
  // input. The hidden `<ListboxFilter>` flips the listbox's `focusable`
  // flag off, so we can drive cmdk's highlight via `commandEl.highlightItem`
  // from `highlightedIndex` without ever losing input focus.
  const commandEl = useTemplateRef<{
    highlightedElement: { value: HTMLElement | null }
    highlightItem: (value: string) => void
  }>('commandEl')
  /**
   * Index into {@link filtered} the user has keyboard-navigated to. `-1`
   * means no pre-highlight — Enter then commits the typed value instead of
   * selecting. Reset on input and on close; seeded to the current selection
   * on open.
   */
  const highlightedIndex = ref(-1)

  /**
   * The text shown in the input. It's a local draft so name-search typing
   * filters the list without committing per keystroke (the model only
   * changes on selection or commit). Synced from `modelValue` whenever the
   * committed value changes — typing never emits, so this won't clobber an
   * in-progress search.
   */
  const draft = ref(displayEstimatedTimeChoice(props.modelValue))
  watch(
    () => props.modelValue,
    (value) => {
      draft.value = displayEstimatedTimeChoice(value)
    }
  )

  const mode = computed(() => classifyEstimatedTimeInput(draft.value))

  /** Entries shown in the dropdown — none in exact mode (so a typed time
   * "clears" the list), all when empty, filtered while name-searching. */
  const filtered = computed<EstimatedTimeNameRef[]>(() => {
    if (mode.value === 'empty') return entries.value
    if (mode.value === 'named-search')
      return filterEstimatedTimeEntries(entries.value, draft.value)
    // Exact mode suppresses the list while a time is being typed; the chevron
    // can still open it explicitly to browse/switch to a named entry.
    return isOpen.value ? entries.value : []
  })

  const selectedName = computed(() =>
    props.modelValue?.kind === 'named' ? props.modelValue.name : undefined
  )

  const isInvalid = computed(
    () => props.ariaInvalid === true || props.modelValue?.kind === 'invalid'
  )

  function onInput(event: Event) {
    draft.value = (event.target as HTMLInputElement).value
    // Typing shifts intent toward "commit what I typed" — drop any popover
    // highlight so Enter commits rather than selecting.
    highlightedIndex.value = -1
    // A typed clock time suppresses the named list entirely.
    isOpen.value =
      !props.disabled && classifyEstimatedTimeInput(draft.value) !== 'exact'
  }

  function commit() {
    const highlighted =
      highlightedIndex.value >= 0
        ? filtered.value[highlightedIndex.value]
        : undefined
    emit(
      'update:modelValue',
      resolveEstimatedTimeChoice({
        text: draft.value,
        entries: entries.value,
        highlighted
      })
    )
  }

  function selectEntry(entry: EstimatedTimeNameRef) {
    emit('update:modelValue', { kind: 'named', name: entry.name })
    isOpen.value = false
  }

  // Focusing the input opens the popover and selects the existing value so
  // it can be overwritten immediately. The hidden `<ListboxFilter>` keeps
  // reka from auto-focusing an item, so no refocus retry is needed.
  function onFocus(event: FocusEvent) {
    ;(event.target as HTMLInputElement).select()
    if (!props.disabled && mode.value !== 'exact') isOpen.value = true
  }

  function onBlur() {
    commit()
    emit('blur')
  }

  // Mousedown anywhere in the anchor (border, padding, clock icon) opens the
  // popover and forwards focus to the input. The chevron button is excluded
  // — it owns its own toggle. `preventDefault` stops the div mousedown from
  // defocusing the input we're about to focus.
  function onAnchorMousedown(event: MouseEvent) {
    if (props.disabled) return
    const target = event.target as HTMLElement
    if (target.closest('button')) return
    // Don't pop an empty list open for an exact time — focus only. The
    // chevron is the explicit "browse named times" affordance.
    if (mode.value !== 'exact') isOpen.value = true
    if (target === inputEl.value) return
    event.preventDefault()
    inputEl.value?.focus()
  }

  function onChevronClick(event: MouseEvent) {
    event.stopPropagation()
    if (props.disabled) return
    isOpen.value = !isOpen.value
    if (isOpen.value) nextTick(() => inputEl.value?.focus())
  }

  /**
   * Arrow nav while the input keeps focus: open the popover (if closed) and
   * move the highlight over {@link filtered}, wrapping at the ends.
   */
  function onArrowNavigation(event: KeyboardEvent) {
    if (filtered.value.length === 0) return
    event.preventDefault()
    isOpen.value = true
    const lastIndex = filtered.value.length - 1
    if (event.key === 'ArrowDown') {
      highlightedIndex.value =
        highlightedIndex.value >= lastIndex ? 0 : highlightedIndex.value + 1
    } else {
      highlightedIndex.value =
        highlightedIndex.value <= 0 ? lastIndex : highlightedIndex.value - 1
    }
  }

  function onEnterKey(event: KeyboardEvent) {
    event.preventDefault()
    const highlighted =
      highlightedIndex.value >= 0
        ? filtered.value[highlightedIndex.value]
        : undefined
    if (highlighted) {
      selectEntry(highlighted)
      return
    }
    commit()
    isOpen.value = false
  }

  function onInputKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      onArrowNavigation(event)
      return
    }
    if (event.key === 'Enter') {
      onEnterKey(event)
      return
    }
    if (event.key === 'Tab') {
      commit()
      isOpen.value = false
      return
    }
    if (event.key === 'Escape' && isOpen.value) {
      isOpen.value = false
      event.stopPropagation()
    }
  }

  // Seed the highlight to the selected entry on open; clear it on close so
  // the next open doesn't reuse stale state.
  watch(isOpen, (open) => {
    if (!open) {
      highlightedIndex.value = -1
      return
    }
    const name = selectedName.value
    highlightedIndex.value = name
      ? filtered.value.findIndex((entry) => entry.name === name)
      : -1
  })

  // Drive reka's internal listbox highlight from `highlightedIndex` so cmdk's
  // `data-highlighted` (the `bg-accent` styling) tracks keyboard nav. With
  // `<ListboxFilter>` making the listbox non-focusable, this won't steal
  // focus from the input.
  watch(highlightedIndex, async (idx) => {
    await nextTick()
    const cmdk = commandEl.value
    if (!cmdk) return
    if (idx < 0) {
      cmdk.highlightedElement.value = null
      return
    }
    const entry = filtered.value[idx]
    if (entry) cmdk.highlightItem(entry.name)
  })

  /** Keep the popover open for interactions inside the anchor (input,
   * chevron); genuine outside clicks fall through and close it. */
  function onInteractOutside(event: Event) {
    const target = (event as unknown as { detail?: { originalEvent?: Event } })
      .detail?.originalEvent?.target as Node | null
    if (target && anchorEl.value?.contains(target)) {
      event.preventDefault()
    }
  }
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverAnchor as-child>
      <div
        ref="anchorEl"
        :class="[
          'border-input bg-background focus-within:border-ring focus-within:ring-ring/50 flex items-center gap-1.5 rounded-md border px-3 py-1 text-sm transition-colors focus-within:ring-[3px]',
          triggerClass,
          disabled && 'pointer-events-none cursor-not-allowed opacity-50',
          isInvalid &&
            'border-destructive focus-within:border-destructive focus-within:ring-destructive/20'
        ]"
        @mousedown="onAnchorMousedown"
      >
        <IconEstimatedTime class="text-muted-foreground size-3.5 shrink-0" />
        <input
          :id="id"
          ref="inputEl"
          :value="draft"
          :placeholder="placeholder"
          :disabled="disabled"
          :data-testid="dataTestid"
          :aria-invalid="isInvalid || undefined"
          maxlength="32"
          class="min-w-0 flex-1 bg-transparent tabular-nums outline-none placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
          @input="onInput"
          @keydown="onInputKeydown"
          @focus="onFocus"
          @blur="onBlur"
        />
        <button
          type="button"
          tabindex="-1"
          :disabled="disabled"
          aria-label="Toggle estimated times"
          class="text-muted-foreground hover:text-foreground flex size-4 shrink-0 items-center justify-center"
          @click="onChevronClick"
        >
          <IconChevronDown class="size-3.5 opacity-60" />
        </button>
      </div>
    </PopoverAnchor>
    <PopoverContent
      class="w-56 p-0"
      align="start"
      @open-auto-focus="(e) => e.preventDefault()"
      @interact-outside="onInteractOutside"
    >
      <Command ref="commandEl">
        <!-- Hidden filter: flips the listbox's `focusable` flag off so
             highlight changes don't steal focus from our input. -->
        <ListboxFilter as="span" style="display: none" tabindex="-1" />
        <CommandList>
          <CommandEmpty>
            {{ loadingEstimatedTimeNames ? 'Loading…' : 'No matching times' }}
          </CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="(entry, index) in filtered"
              :key="entry.name"
              :value="entry.name"
              @select="selectEntry(entry)"
              @pointerenter="highlightedIndex = index"
            >
              <IconTimeOfDay
                :time="entry.localReferenceTime"
                class="text-muted-foreground size-4 shrink-0"
              />
              <span class="shrink-0 truncate text-sm font-medium">{{
                entry.name
              }}</span>
              <span class="text-muted-foreground flex-1 truncate text-xs">{{
                entry.description
              }}</span>
              <span class="text-muted-foreground text-xs tabular-nums">{{
                entry.localReferenceTime.slice(0, 5)
              }}</span>
              <IconCheck
                :class="[
                  'ml-1 size-3.5 shrink-0',
                  selectedName === entry.name ? 'opacity-100' : 'opacity-0'
                ]"
              />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
